import { useAuth } from "../lib/auth";
import TaskList from "./TaskList";
import MyOfficeTile from "../components/MyOfficeTile";
import ComplianceAlerts from "../components/ComplianceAlerts";
import PushHealthCard from "../components/PushHealthCard";
import MarginHistoryChart from "../components/MarginHistoryChart";
import ActivityStreakChip from "../components/ActivityStreakChip";
import SocialQuotaCard from "../components/SocialQuotaCard";
import ImportantDaysCard from "../components/ImportantDaysCard";
import PendingApprovalsCard from "../components/PendingApprovalsCard";
import DigestReachTile from "../components/DigestReachTile";
import ReviewQueueTile from "../components/ReviewQueueTile";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Landmark, MapPin, Plane, Handshake, Star, TrendingUp, Pencil, Check, X, CalendarCheck, ExternalLink, Video, ClipboardCheck, Bell, ShieldCheck, MessageCircle, AlertTriangle, Sparkles, ArrowRight } from "lucide-react";

const ROLE_LABEL = { FOUNDER: "Founder / CEO", BDOM: "BD & Ops Manager", PCWM: "Placement & Cultural", GEHM: "Guest Exp & Housekeeping", SMCM: "Social Media & Content" };
const CUR_SYM = { USD: "$", EUR: "€", GBP: "£", INR: "₹" };

const TASK_TITLES = { FOUNDER: "All team tasks", BDOM: "My team's tasks" };
const taskTitleFor = (role) => TASK_TITLES[role] || "My tasks";

// ---------------------------------------------------------------------------
// OnboardingBanner — soft prompt shown to a Founder while the tenant is
// still on the generic-defaults brand config. Dismissible; dismiss state
// lives in localStorage so it doesn't nag every reload. Auto-refetches
// on `aii:profile_updated` so if the Founder finishes the wizard, the
// banner disappears without a manual refresh.
// ---------------------------------------------------------------------------
function OnboardingBanner() {
  const [needs, setNeeds] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("aii.onb-dismissed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/brand/onboarding-state")
      .then(r => alive && setNeeds(!!r.data?.needs_onboarding))
      .catch(() => alive && setNeeds(false));
    load();
    const h = () => load();
    window.addEventListener("aii:profile_updated", h);
    return () => { alive = false; window.removeEventListener("aii:profile_updated", h); };
  }, []);
  const dismiss = () => {
    try { localStorage.setItem("aii.onb-dismissed", "1"); } catch { /* ignore */ }
    setDismissed(true);
  };
  if (!needs || dismissed) return null;
  return (
    <div
      data-testid="dashboard-onboarding-banner"
      className="rounded-xl p-4 sm:p-5 flex items-start sm:items-center justify-between gap-3 flex-wrap
                 bg-gradient-to-r from-brand/10 via-emerald-50 to-emerald-100 border border-brand/30 shadow-sm">
      <div className="flex items-start gap-3 min-w-0">
        <div className="w-9 h-9 rounded-full bg-brand text-white flex items-center justify-center shrink-0">
          <Sparkles size={16}/>
        </div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-brand font-bold">Welcome — one thing first</div>
          <div className="font-display text-lg font-semibold text-slate-900">
            Personalise your workspace in under 60 seconds
          </div>
          <div className="text-xs text-slate-700 mt-0.5">
            Pick your logo, primary colour, tagline and a login hero photo — everything is optional and you can change it later from Settings.
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={dismiss} data-testid="dashboard-onboarding-dismiss"
                className="text-xs px-3 py-1.5 text-slate-600 hover:text-slate-900">
          Not now
        </button>
        <Link to="/onboarding" data-testid="dashboard-onboarding-start"
              className="text-xs px-4 py-2 bg-brand text-white font-semibold rounded-md hover:bg-brand-dark flex items-center gap-1">
          Start setup <ArrowRight size={12}/>
        </Link>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ programs: 0, weekends: 0, arrivals: 0, partners: 0 });
  const [teamProgress, setTeamProgress] = useState({});
  const [summary, setSummary] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [meetings, setMeetings] = useState([]);

  useEffect(() => {
    (async () => {
      const [p, w, a, pr, t, sum, rt, mt] = await Promise.all([
        api.get("/program-bookings"),
        api.get("/weekend-bookings"),
        api.get("/arrivals"),
        api.get("/partners").catch(() => ({ data: [] })),
        api.get("/tasks"),
        api.get("/sales/summary").catch(() => ({ data: null })),
        api.get("/ratings").catch(() => ({ data: [] })),
        api.get("/partner-meetings").catch(() => ({ data: [] })),
      ]);
      // Match the Arrivals page's default "Active" tab: only count
      // arrivals whose flight_date is today or later (or blank — those
      // are unscheduled and still awaiting flight info). Previously we
      // counted every row in the DB including past ones, so the KPI on
      // the Founder dashboard read a much bigger number than what the
      // Arrivals list actually showed.
      const today = new Date().toISOString().slice(0, 10);
      const activeArrivals = (a.data || []).filter(r => !r.flight_date || r.flight_date >= today);
      // Same logic for programme + weekend bookings — hide bookings that
      // fully ended before today so the KPI matches the list default.
      const activePrograms = (p.data || []).filter(r => !r.end_date || r.end_date >= today);
      const activeWeekends = (w.data || []).filter(r => !r.end_dates || r.end_dates >= today);
      setStats({ programs: activePrograms.length, weekends: activeWeekends.length, arrivals: activeArrivals.length, partners: pr.data.length });
      const byRole = {};
      for (const role of ["FOUNDER","BDOM","PCWM","GEHM","SMCM"]) {
        const rti = t.data.filter(x => x.assignee_role === role);
        byRole[role] = { total: rti.length, done: rti.filter(x=>x.done).length };
      }
      setTeamProgress(byRole);
      setSummary(sum.data);
      setRatings(rt.data);
      setMeetings(mt.data);
    })();
  }, []);

  const role = user.effective_role;
  const isFounder = role === "FOUNDER";
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(user.name);

  const saveName = async () => {
    const trimmed = (nameDraft || "").trim();
    if (!trimmed) { setEditingName(false); return; }
    await api.patch("/me/profile", { name: trimmed });
    window.dispatchEvent(new CustomEvent("aii:profile_updated"));
    setEditingName(false);
  };

  const cards = [
    { label: "Program Bookings", value: stats.programs, icon: Landmark, roles: ["FOUNDER","BDOM","PCWM","SMCM"] },
    { label: "Weekend Bookings", value: stats.weekends, icon: MapPin, roles: ["FOUNDER","BDOM","PCWM","SMCM"] },
    { label: "Arrivals", value: stats.arrivals, icon: Plane, roles: ["FOUNDER","BDOM","GEHM","PCWM","SMCM"], hint: "Upcoming (flight date today or later)" },
    { label: "Partners", value: stats.partners, icon: Handshake, roles: ["FOUNDER","BDOM"] },
  ].filter(c => c.roles.includes(role));

  const currentMonth = new Date().toISOString().slice(0, 7);
  const monthOf = (obj) => obj?.[currentMonth] || { USD: 0, EUR: 0, GBP: 0, INR: 0 };

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      {/* Onboarding nudge — shows only for a Founder on a generic-brand
          workspace, dismissible via localStorage so it doesn't nag. */}
      {role === "FOUNDER" && <OnboardingBanner/>}
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Overview · {ROLE_LABEL[role]}</div>
          {editingName ? (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-3xl font-semibold">Good day,</span>
              <input data-testid="dash-name-input" autoFocus value={nameDraft} onChange={e=>setNameDraft(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") saveName(); if (e.key === "Escape") { setEditingName(false); setNameDraft(user.name); } }}
                className="text-3xl font-semibold px-2 py-1 border border-[color:var(--aii-border)] rounded-md" />
              <button data-testid="dash-name-save" onClick={saveName} className="p-1.5 bg-brand text-white rounded-md hover:bg-brand-dark"><Check size={14} /></button>
              <button onClick={()=>{ setEditingName(false); setNameDraft(user.name); }} className="p-1.5 text-[color:var(--aii-muted)] hover:text-red-600"><X size={14} /></button>
            </div>
          ) : (
            <h1 className="text-3xl font-semibold mt-1 flex items-center gap-2">
              Good day, {user.name}.
              <button data-testid="dash-name-edit" onClick={()=>{ setNameDraft(user.name); setEditingName(true); }} title="Edit your name" className="text-[color:var(--aii-muted)] hover:text-brand p-1 rounded"><Pencil size={14} /></button>
            </h1>
          )}
          {/* Weekly activity streak chip — every PCWM/GEHM/SMCM sees their
              own office; Founder/BDOM see all offices unless they filter. */}
          {(role === "PCWM" || role === "FOUNDER" || role === "BDOM") && (
            <div className="mt-2"><ActivityStreakChip /></div>
          )}
        </div>
        <div className="text-sm text-[color:var(--aii-text-2)]">{new Date().toLocaleDateString(undefined, { weekday:'long', month:'long', day:'numeric' })}</div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(({label,value,icon:Icon}) => (
          <div key={label} className="aii-card p-5" data-testid={`stat-${label}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-[color:var(--aii-muted)]">{label}</div>
              <Icon size={16} className="text-brand" />
            </div>
            <div className="text-3xl font-display font-semibold">{value}</div>
          </div>
        ))}
      </div>

      {/* Partner submissions waiting for Founder/BDOM decision — shown
          at the very top of the dashboard so no program booking or
          weekend tour request sits idle. Hides itself when the queue
          is empty. */}
      {(role === "FOUNDER" || role === "BDOM") && (
        <PendingApprovalsCard/>
      )}

      <MyOfficeTile />

      {/* Founder + BDOM only — surfaces arrivals landing in <48h with no
          pickup assigned so ops can chase GEHM before the participant
          lands with nobody to greet them. */}
      {(role === "FOUNDER" || role === "BDOM") && (
        <UnsolicitedArrivalsCard/>
      )}

      <ComplianceAlerts />

      {/* SMCM + BDOM + Founder — weekly quota + festival calendar */}
      {(role === "SMCM" || role === "FOUNDER" || role === "BDOM") && (
        <>
          <SocialQuotaCard />
          <ImportantDaysCard />
        </>
      )}

      {/* Founder-only ops-quality widgets */}
      {isFounder && <PushHealthCard />}
      {isFounder && <MarginHistoryChart />}

      {/* Revenue widgets — Founder only (BDOM excluded per Founder request) */}
      {role === "FOUNDER" && summary && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2"><TrendingUp size={18} className="text-brand" /> Revenue</h3>
          <div className="grid md:grid-cols-4 gap-4">
            {["USD","EUR","GBP","INR"].map(c => (
              <div key={c} className="aii-card p-5" data-testid={`rev-${c}`}>
                <div className="text-xs uppercase tracking-wider text-[color:var(--aii-muted)]">Total {c}</div>
                <div className="text-3xl font-display font-semibold mt-1">{CUR_SYM[c]}{summary.total[c].toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                <div className="mt-3 space-y-1 text-xs text-[color:var(--aii-text-2)]">
                  <div className="flex justify-between"><span>Programs</span><span className="font-semibold">{CUR_SYM[c]}{summary.program[c].toLocaleString(undefined,{maximumFractionDigits: 0})}</span></div>
                  <div className="flex justify-between"><span>Weekends</span><span className="font-semibold">{CUR_SYM[c]}{summary.weekend[c].toLocaleString(undefined,{maximumFractionDigits: 0})}</span></div>
                  <div className="flex justify-between"><span>Activities</span><span className="font-semibold">{CUR_SYM[c]}{summary.activity[c].toLocaleString(undefined,{maximumFractionDigits: 0})}</span></div>
                  <div className="flex justify-between"><span>Trips</span><span className="font-semibold">{CUR_SYM[c]}{(summary.trip?.[c]||0).toLocaleString(undefined,{maximumFractionDigits: 0})}</span></div>
                </div>
              </div>
            ))}
          </div>
          <div className="aii-card p-5">
            <div className="text-xs uppercase tracking-wider text-[color:var(--aii-muted)] mb-2">This month ({currentMonth})</div>
            <div className="grid sm:grid-cols-4 gap-3 text-sm">
              {["USD","EUR","GBP","INR"].map(c => {
                const total = (monthOf(summary.monthly_program)[c]||0) + (monthOf(summary.monthly_weekend)[c]||0) + (monthOf(summary.monthly_activity)[c]||0);
                return (
                  <div key={c} className="border border-[color:var(--aii-border)] rounded-lg p-3">
                    <div className="text-xs text-[color:var(--aii-muted)]">Monthly {c}</div>
                    <div className="font-display text-2xl font-semibold">{CUR_SYM[c]}{total.toLocaleString(undefined,{maximumFractionDigits: 0})}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Team ratings — Founder only */}
      {role === "FOUNDER" && ratings.length > 0 && (
        <div className="aii-card p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Star size={18} className="text-brand" /> Team ratings (task-completion frequency)</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ratings.map(r => (
              <div key={r.user_id} className="border border-[color:var(--aii-border)] rounded-lg p-4" data-testid={`rating-${r.role}`}>
                <div className="text-xs text-[color:var(--aii-muted)] uppercase tracking-wider">{r.role}</div>
                <div className="font-semibold mt-0.5">{r.name}</div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="text-2xl font-display font-semibold text-brand">{r.rating.toFixed(1)}</div>
                  <div className="flex">
                    {Array.from({length:5}).map((_,i) => (
                      <Star key={`${r.user_id}-star-${i}`} size={14} className={i < Math.round(r.rating) ? "fill-[#1B7A3E] text-[#1B7A3E]" : "text-[color:var(--aii-border)]"} />
                    ))}
                  </div>
                </div>
                <div className="text-xs text-[color:var(--aii-muted)] mt-1">{r.done}/{r.total} done · {r.recent_14d} in 14d</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isFounder && (
        <EmployeeTasksReport/>
      )}
      {isFounder && (
        <div className="aii-card p-5 bg-gradient-to-br from-emerald-50 to-white border-emerald-200" data-testid="dash-staff-hub-cta">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Team resources</div>
              <h3 className="font-display font-semibold text-lg">Staff Hub</h3>
              <p className="text-xs text-[color:var(--aii-muted)] mt-0.5">HR policies · Employee handbook · Training videos · Marketing essentials · Job descriptions.</p>
            </div>
            <a href="/staff-hub" className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5">Open →</a>
          </div>
        </div>
      )}
      {isFounder && <DigestReachTile />}
      {(user.effective_role === "FOUNDER" || user.effective_role === "BDOM") && <ReviewQueueTile />}
      {role === "FOUNDER" && (
        <div className="aii-card p-6">
          <h3 className="text-lg font-semibold mb-4">Team progress today</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {Object.entries(teamProgress).map(([r, s]) => (
              <div key={r} className="border border-[color:var(--aii-border)] rounded-lg p-4" data-testid={`progress-${r}`}>
                <div className="text-xs uppercase tracking-wider text-[color:var(--aii-muted)]">{ROLE_LABEL[r]}</div>
                <div className="font-display font-semibold text-2xl mt-1">{s.done}/{s.total}</div>
                <div className="h-1.5 bg-[color:var(--aii-bg)] rounded-full mt-2 overflow-hidden">
                  <div className="h-full bg-brand" style={{ width: `${s.total ? (s.done/s.total)*100 : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(role === "FOUNDER" || role === "BDOM") && (
        <div className="aii-card p-6" data-testid="meetings-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold flex items-center gap-2"><CalendarCheck className="text-brand" size={18} /> Upcoming partner meetings</h3>
            <span className="text-xs text-[color:var(--aii-muted)]">{meetings.filter(m => m.status !== "cancelled" && m.status !== "done").length} open</span>
          </div>
          {meetings.length === 0 ? (
            <div className="text-sm text-[color:var(--aii-muted)] py-4">No meetings booked yet. Send an outreach email from <b>Partners in Progress</b> — bookings will land here automatically.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="aii-table">
                <thead>
                  <tr>
                    <th>Date · Time</th>
                    <th>Partner</th>
                    <th>Contact</th>
                    <th>Topic</th>
                    <th>Status</th>
                    <th className="text-right">Join</th>
                  </tr>
                </thead>
                <tbody>
                  {meetings.slice(0, 8).map((m) => (
                    <tr key={m.id} data-testid={`meeting-${m.id}`}>
                      <td className="whitespace-nowrap font-medium">{m.proposed_date} · {m.proposed_time}{m.timezone_label ? ` ${m.timezone_label}` : ""}</td>
                      <td><div className="font-medium">{m.partner_company}</div><div className="text-xs text-[color:var(--aii-muted)]">{m.partner_country}</div></td>
                      <td><div>{m.contact_name}</div><a href={`mailto:${m.contact_email}`} className="text-xs text-brand hover:underline">{m.contact_email}</a></td>
                      <td className="text-xs max-w-[220px]">{m.topic || <span className="text-[color:var(--aii-muted)]">—</span>}</td>
                      <td>
                        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded ${
                          m.status === "confirmed" ? "bg-emerald-100 text-emerald-800"
                          : m.status === "done" ? "bg-slate-100 text-slate-700"
                          : m.status === "cancelled" ? "bg-red-100 text-red-800"
                          : "bg-amber-100 text-amber-800"
                        }`}>{m.status}</span>
                      </td>
                      <td className="text-right">
                        {m.meet_link ? (
                          <a href={m.meet_link} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 bg-brand text-white rounded hover:bg-brand-dark inline-flex items-center gap-1"><Video size={11} /> Join</a>
                        ) : role === "FOUNDER" || role === "BDOM" ? (
                          <ConfirmMeetingBtn meeting={m} onDone={(patch) => setMeetings((xs) => xs.map((x) => x.id === m.id ? { ...x, ...patch } : x))} />
                        ) : <span className="text-xs text-[color:var(--aii-muted)]">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <TaskList user={user} title={taskTitleFor(role)} />
    </div>
  );
}

function ConfirmMeetingBtn({ meeting, onDone }) {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    if (!link.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/partner-meetings/${meeting.id}`, { status: "confirmed", meet_link: link.trim() });
      onDone?.({ status: "confirmed", meet_link: link.trim() });
      setOpen(false);
      window.location.reload();
    } catch (err) { console.debug("partner-meeting confirm failed:", err?.message); }
    setBusy(false);
  };
  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid={`confirm-${meeting.id}`}
      className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] inline-flex items-center gap-1"><ExternalLink size={11}/> Add link</button>
  );
  return (
    <div className="flex items-center gap-1">
      <input autoFocus value={link} onChange={(e) => setLink(e.target.value)} placeholder="meet.google.com/…"
        className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded w-40" />
      <button onClick={confirm} disabled={busy} className="text-xs px-2 py-1 bg-brand text-white rounded hover:bg-brand-dark disabled:opacity-50">{busy ? "…" : "Save"}</button>
      <button onClick={() => setOpen(false)} className="text-xs text-[color:var(--aii-muted)] px-1">✕</button>
    </div>
  );
}

// -------- Unsolicited Arrivals — Founder + BDOM only ---------------------
// Surfaces arrivals landing in < 48h with no pickup assigned so the founder
// can chase GEHM before the participant lands with nobody to greet them.
// Polls every 90 s to stay live without hammering the API.
function UnsolicitedArrivalsCard() {
  const [rows, setRows] = useState([]);
  const load = async () => {
    try {
      const { data } = await api.get("/dashboard/unsolicited-arrivals");
      setRows(data || []);
    } catch (err) { console.debug("unsolicited-arrivals poll failed:", err?.message); }
  };
  useEffect(() => { load(); const t = setInterval(load, 90 * 1000); return () => clearInterval(t); }, []);
  if (rows.length === 0) return null;
  return (
    <div className="aii-card p-5 border-2 border-red-300 bg-red-50" data-testid="unsolicited-arrivals-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold flex items-center gap-2 text-red-800">
          <AlertTriangle size={18}/> Unsolicited arrivals · {rows.length} landing in &lt;48h with no pickup
        </h3>
        <Link to="/arrivals" className="text-xs text-brand font-semibold hover:underline">Fix in Arrivals →</Link>
      </div>
      <div className="space-y-2">
        {rows.map(r => (
          <div key={r.id} data-testid={`unsolicited-arr-${r.id}`}
               className="text-sm bg-white border border-red-200 rounded p-3 flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-semibold">{r.customer || "—"} <span className="text-[10px] font-normal text-red-600 ml-1">in {r.hours_until.toFixed(1)}h</span></div>
              <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5">
                {r.flight_date} {r.flight_time || ""} · {r.airline || "—"} {r.flight_number || ""} · {r.arrival_airport || "—"}{r.city ? ` → ${r.city}` : ""}
              </div>
              {r.telephone && <div className="text-[11px] text-[color:var(--aii-muted)]">☎ {r.telephone}</div>}
            </div>
            <Link to="/arrivals" className="text-[11px] px-2 py-1 bg-red-600 text-white rounded font-semibold self-center whitespace-nowrap">Resolve</Link>
          </div>
        ))}
      </div>
    </div>
  );
}



// -------- Founder Dashboard: per-employee task completion snapshot --------
function EmployeeTasksReport() {
  const [rows, setRows] = useState([]);
  const [celebrations, setCelebrations] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [r, c] = await Promise.all([
        api.get("/founder/employee-tasks-report"),
        api.get("/founder/celebrations").catch(() => ({ data: [] })),
      ]);
      setRows(r.data || []);
      setCelebrations(c.data || []);
    } catch (err) { console.debug("employee-tasks-report / celebrations poll failed:", err?.message); }
  };
  useEffect(() => { load(); const t = setInterval(load, 60 * 1000); return () => clearInterval(t); }, []);

  const runReminders = async () => {
    setBusy(true);
    try { await api.post("/founder/task-reminders/run-now"); toast.success("Reminder emails queued"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const runDigest = async () => {
    setBusy(true);
    try { await api.post("/founder/digest/run-now"); toast.success("Digest sent to your inbox"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  // Cohort Digest via WhatsApp — compiles a monthly break-down of program
  // bookings + arrivals + weekend bookings into a compact text summary and
  // opens a wa.me deep-link so the founder can share it with any contact.
  // No server round-trip, no Twilio dependency — reuses the same lists the
  // dashboard already exposes.  Falls back gracefully if a fetch fails.
  const shareCohortDigestWA = async () => {
    setBusy(true);
    try {
      const [p, a, w] = await Promise.all([
        api.get("/program-bookings").catch(() => ({ data: [] })),
        api.get("/arrivals").catch(() => ({ data: [] })),
        api.get("/weekend-bookings").catch(() => ({ data: [] })),
      ]);
      const groupByMonth = (list, dateKey) => {
        const g = new Map();
        for (const r of list) {
          const k = ((r[dateKey] || "") + "").slice(0, 7);
          if (!k || k.length < 7) continue;
          g.set(k, (g.get(k) || 0) + 1);
        }
        return [...g.entries()].sort(([a], [b]) => a.localeCompare(b));
      };
      const bookings = groupByMonth(p.data, "start_date");
      const arrivals = groupByMonth(a.data, "flight_date");
      const weekends = groupByMonth(w.data, "tour_date");
      const today = new Date().toISOString().slice(0, 10);
      const lines = [
        `🌿 Aii Group cohort digest — ${today}`,
        ``,
        `Totals: ${p.data.length} programme bookings · ${a.data.length} arrivals · ${w.data.length} weekend bookings`,
      ];
      if (bookings.length) {
        lines.push(``, `📅 Program bookings by month:`);
        bookings.slice(0, 6).forEach(([ym, n]) => lines.push(`  • ${ym} · ${n}`));
      }
      if (arrivals.length) {
        lines.push(``, `✈️ Arrivals by month:`);
        arrivals.slice(0, 6).forEach(([ym, n]) => lines.push(`  • ${ym} · ${n}`));
      }
      if (weekends.length) {
        lines.push(``, `🏝️ Weekend bookings by month:`);
        weekends.slice(0, 6).forEach(([ym, n]) => lines.push(`  • ${ym} · ${n}`));
      }
      lines.push(``, `— Sent from Aii Group Team OS`);
      const msg = encodeURIComponent(lines.join("\n"));
      // Copy to clipboard as a safety net so the founder can paste it into
      // any chat if the wa.me tab is blocked by a pop-up blocker.
      try { await navigator.clipboard.writeText(decodeURIComponent(msg)); } catch { /* clipboard blocked */ }
      window.open(`https://wa.me/?text=${msg}`, "_blank");
      toast.success("Cohort digest opened in WhatsApp — pick a contact & send");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build digest"); }
    finally { setBusy(false); }
  };

  const totalDoneToday = rows.reduce((s, r) => s + (r.done_today || 0), 0);
  const totalPending = rows.reduce((s, r) => s + (r.pending || 0), 0);
  const totalOverdue = rows.reduce((s, r) => s + (r.overdue || 0), 0);
  const totalApprovals = rows.reduce((s, r) => s + (r.pending_approvals || 0), 0);

  return (
    <div className="aii-card p-5" data-testid="employee-tasks-report">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Today at Aii Group</div>
          <h3 className="font-display font-semibold text-lg flex items-center gap-2"><ClipboardCheck size={18} className="text-brand"/> Team tasks — daily snapshot</h3>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={runDigest} disabled={busy} data-testid="digest-run" className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)]"><Bell size={11} className="inline mr-1"/> Email me digest</button>
          <button onClick={shareCohortDigestWA} disabled={busy} data-testid="cohort-digest-wa"
                  title="Build a monthly cohort recap and share it via WhatsApp"
                  className="text-xs px-3 py-1.5 border border-emerald-600 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md flex items-center gap-1">
            <MessageCircle size={11}/> Cohort → WhatsApp
          </button>
          <button onClick={runReminders} disabled={busy} data-testid="reminders-run" className="text-xs px-3 py-1.5 bg-brand text-white rounded-md hover:bg-brand-dark disabled:opacity-50">Remind everyone</button>
        </div>
      </div>
      {celebrations.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 p-3" data-testid="task-celebrations">
          <div className="text-[10px] uppercase tracking-widest text-amber-800 font-bold mb-1">🎉 Cleared their queue · past 24h</div>
          <div className="flex flex-wrap gap-2">
            {celebrations.map(c => (
              <div key={c.id} data-testid={`celebration-${c.user_id}`} className="inline-flex items-center gap-1.5 bg-white border border-amber-300 rounded-full px-3 py-1 shadow-sm text-xs">
                <span>🎉</span>
                <span className="font-semibold text-amber-900">{c.user_name}</span>
                <span className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">{c.role}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3">
          <div className="text-2xl font-display font-bold text-emerald-700">{totalDoneToday}</div>
          <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold">Done today</div>
        </div>
        <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
          <div className="text-2xl font-display font-bold text-amber-700">{totalPending}</div>
          <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold">Pending</div>
        </div>
        <div className={`rounded-md p-3 border ${totalOverdue > 0 ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`}>
          <div className={`text-2xl font-display font-bold ${totalOverdue > 0 ? "text-red-700" : "text-slate-500"}`}>{totalOverdue}</div>
          <div className={`text-[11px] uppercase tracking-widest font-semibold ${totalOverdue > 0 ? "text-red-800" : "text-slate-500"}`}>Overdue</div>
        </div>
        <Link to="/attendance" data-testid="etr-approvals-tile"
          className={`rounded-md p-3 border transition group ${totalApprovals > 0 ? "bg-amber-50 border-amber-300 hover:bg-amber-100" : "bg-slate-50 border-slate-200 hover:bg-slate-100"}`}
          title="Self-marked staff attendance awaiting your sign-off">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className={totalApprovals > 0 ? "text-amber-700" : "text-slate-400"}/>
            <div className={`text-2xl font-display font-bold ${totalApprovals > 0 ? "text-amber-700" : "text-slate-500"}`}>{totalApprovals}</div>
          </div>
          <div className={`text-[11px] uppercase tracking-widest font-semibold ${totalApprovals > 0 ? "text-amber-800" : "text-slate-500"}`}>Approvals pending</div>
        </Link>
      </div>
      <div className="space-y-1.5">
        {rows.length === 0 && <div className="text-sm text-[color:var(--aii-muted)] py-4 text-center">Loading…</div>}
        {rows.map(r => (
          <div key={r.user_id} data-testid={`etr-${r.user_id}`} className="flex items-center gap-3 py-2 border-b border-[color:var(--aii-border)] last:border-b-0">
            <div className="w-8 h-8 rounded-full bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)] font-bold flex items-center justify-center text-xs">{(r.name || "?")[0]}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{r.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">{r.role}</div>
            </div>
            <div className="text-xs flex items-center gap-3">
              <span className="text-emerald-700 font-semibold">{r.done_today} done</span>
              <span className="text-amber-700 font-semibold">{r.pending} pending</span>
              {r.overdue > 0 && <span className="text-red-700 font-bold">{r.overdue} overdue</span>}
              {r.pending_approvals > 0 && (
                <span data-testid={`etr-approvals-${r.user_id}`} className="text-amber-800 font-semibold inline-flex items-center gap-0.5" title="Self-marked attendance awaiting your sign-off">
                  <ShieldCheck size={11}/> {r.pending_approvals} approvals
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
